import React, { PropsWithChildren, memo, ReactNode, forwardRef, Ref } from 'react';
import classNames from 'classnames';
import Label from '@/shared/Label';
import CheckboxChecked from '@/components/Icons/CheckboxChecked';
import CheckboxUnchecked from '@/components/Icons/CheckboxUnchecked';
import Span from '../Span';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import Paragraph from '../Paragraph';
import { FieldError } from 'react-hook-form';

interface StateOfCheckBox {
  checked?: boolean;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface CheckBoxProps extends StateOfCheckBox {
  id: string;
  className?: string;
  dataTestId?: string;
  render?: () => ReactNode;
  errors?: any;
  name: string;
  label?: string;
}

const Checkbox = (props: PropsWithChildren<CheckBoxProps>, ref: Ref<HTMLInputElement>) => {
  const { id, name, checked, className, render, dataTestId, errors, label, ...restOfProps } = props;
  const fieldError: FieldError | undefined = (errors?.[name] as FieldError) ?? undefined;
  const errorMessage = fieldError?.message || VALIDATION_ERRORS[fieldError?.type];
  return (
    <>
      <Label htmlFor={id} className={classNames('cursor-pointer', className)}>
        {
          <input
            ref={ref}
            type="checkbox"
            id={id}
            name={id}
            checked={checked}
            data-testid={dataTestId}
            {...restOfProps}
            className="w-0 h-0 opacity-0"
          />
        }
        <Span className="w-s6">{checked ? <CheckboxChecked /> : <CheckboxUnchecked />}</Span>
        {label ? <Span className="text-body-sm text-start">{label}</Span> : null}
        {!label && typeof render === 'function' && render()}
      </Label>
      {errorMessage ? <Paragraph className=" text-cu-negative">{errorMessage}</Paragraph> : null}
    </>
  );
};

export default memo(forwardRef(Checkbox));
